import type { StrategyFactsheetResponseDTO } from '@/api-v2/web/strategies';
import { isStrategyFactsheetResponseDTO } from '@/api-v2/web/strategies';
import type { IItemUnderAnalysis } from '@/types/IItemUnderAnalysis';
import type { IPortfolioTree, IPortfolioTreeSubportfolio, IPortfolioTreeStrategy } from '@/types/IPortfolioTree';
import { isPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import { findParentSubportfolio } from '@/utils/portfolioTree';

/**
 * Finds the benchmark on the subportfolio
 * If no benchmark is found, go up the tree until a valid benchmark is found
 * Otherwise return null
 */
export const findValidBenchmark = (
  portfolioTree: IPortfolioTree,
  item: IPortfolioTreeSubportfolio | IPortfolioTreeStrategy,
  debug?: boolean,
): string | null => {
  if (debug) {
    console.log('findValidBenchmark re-running');
  }

  if (isPortfolioTreeSubportfolio(item)) {
    if (item.benchmark) {
      if (debug) console.log('findValidBenchmark returning', item.benchmark);

      return item.benchmark;
    }
  }

  // if we are on the master portfolio tree but no benchmark was found above, return null
  if (portfolioTree.portfolioId === item.portfolioTreeId) {
    if (debug) {
      console.log('findValidBenchmark returning null because portfolioTree.portfolioId === item.portfolioTreeId');
    }

    return null;
  }

  const parentOfItem = findParentSubportfolio(portfolioTree.portfolioTree, item.portfolioTreeId);
  if (!parentOfItem) {
    if (debug) {
      console.log('findValidBenchmark returning null because parentOfItem is null');
    }

    return null;
  }

  if (debug) {
    console.log('findValidBenchmark re-running with parentOfItem');
  }

  return findValidBenchmark(portfolioTree, parentOfItem, debug);
};

export const getSimpleBenchmark = (
  factsheetInfo: StrategyFactsheetResponseDTO,
): { name: string; code: string; available: boolean } | null => {
  const availableBenchmarks = new Set(factsheetInfo.availableBenchmarks);

  const defaultBenchmark = factsheetInfo.strategy.benchmarks;
  if (defaultBenchmark) {
    return {
      name: defaultBenchmark.name,
      code: defaultBenchmark.code,
      available: availableBenchmarks.has(defaultBenchmark.code),
    };
  }

  const privateFundBenchmarks = getPrivateFundBenchmarks(factsheetInfo);

  if (privateFundBenchmarks.length === 1 && privateFundBenchmarks[0].weight === '100%') {
    return privateFundBenchmarks[0];
  }

  return null;
};

export const getPrivateFundBenchmarks = (
  factsheetInfo: StrategyFactsheetResponseDTO,
): { name: string; code: string; weight: string; available: boolean }[] => {
  const availableBenchmarks = factsheetInfo.availableFundBenchmarks;

  const benchmarkObj = factsheetInfo.mstarFundInfo?.benchmarks;
  if (benchmarkObj) {
    const primaryBenchmark = benchmarkObj.primary_prospectus_benchmarks;
    if (primaryBenchmark) {
      const decimalPlaces = Math.max(...primaryBenchmark.map((o) => o.toString().split('.')?.[1]?.length || 0));
      return primaryBenchmark.map((benchmark): { name: string; code: string; weight: string; available: boolean } => {
        // Map the morningstarId to the code used on the platform
        const code = availableBenchmarks.find((o) => o.morningstarId === benchmark.id)?.code;
        return {
          name: benchmark.name,
          code: code ?? benchmark.id,
          weight: `${(benchmark.weight * 100).toFixed(decimalPlaces)}%`,
          available: code != null,
        };
      });
    }
  }

  return [];
};

/**
 * Returns the firstBenchmarkCode according to the conditions defined in WAA-2000
 *
 * This method only considers the default benchmark and the Morningstar benchmark because the user benchmark relies
 * on global state to produce the code or slug.
 *
 * Return `undefined` for an error state
 * Return `null` if the user removes a benchmark
 *
 * @param factsheet
 */
export const findStrategyBenchmarkInternal = ({
  factsheet,
  debug,
}: {
  factsheet?: IItemUnderAnalysis | null;
  debug?: boolean;
}): string | null | undefined => {
  if (!factsheet || !isStrategyFactsheetResponseDTO(factsheet)) return undefined;

  // Load default benchmark automatically if available
  const defaultBenchmark = factsheet.strategy.benchmarks;
  if (defaultBenchmark) {
    if (factsheet.availableBenchmarks.includes(defaultBenchmark.code)) {
      if (debug) console.log('findStrategyBenchmarkInternal returning defaultBenchmark', defaultBenchmark.code);

      return defaultBenchmark.code;
    }

    if (debug) console.log('findStrategyBenchmarkInternal returning UNDEFINED');

    return undefined;
  }

  // Load primary benchmark for funds automatically if available
  const primaryBenchmarks = factsheet.mstarFundInfo?.benchmarks?.primary_prospectus_benchmarks ?? [];
  if (primaryBenchmarks.length === 1) {
    // Load single benchmark (at 100%) only
    const [firstBenchmark] = primaryBenchmarks;
    if (firstBenchmark.weight === 1) {
      const retval = factsheet.availableFundBenchmarks.find((o) => o.morningstarId === firstBenchmark.id)?.code;
      if (debug) console.log('findStrategyBenchmarkInternal returning first primary benchmark', retval);

      return retval;
    }
  }

  if (debug) console.log('findStrategyBenchmarkInternal returning null');

  return null;
};
